// Backfill Parents — one-time admin tool to populate the new parentUserIds
// array field on existing athlete docs (copying from legacy parentUserId).
// Idempotent: re-running is safe. Can be deleted from the codebase once
// the migration is complete and verified.

import React, { useState } from 'react';
import { getFunctions, httpsCallable } from 'firebase/functions';
import Layout from '../components/Layout';
import {
  Box, Typography, Paper, Button, Alert, CircularProgress, Stack,
} from '@mui/material';
import PlayArrowIcon from '@mui/icons-material/PlayArrow';

const DARK = '#1E3A5F';

export default function BackfillParents() {
  const [running, setRunning] = useState(false);
  const [result, setResult] = useState(null);
  const [error, setError] = useState('');

  async function run() {
    setRunning(true);
    setError('');
    setResult(null);
    try {
      const fns = getFunctions();
      const backfill = httpsCallable(fns, 'backfillParentUserIds');
      const r = await backfill();
      setResult(r.data);
    } catch (e) {
      console.error('Backfill error:', e);
      setError(e.message || 'Backfill failed.');
    } finally {
      setRunning(false);
    }
  }

  return (
    <Layout>
      <Box sx={{ p: { xs: 2, md: 3 }, maxWidth: 700, mx: 'auto' }}>
        <Typography variant="h4" sx={{ fontWeight: 700, color: DARK, mb: 1 }}>
          Backfill parentUserIds
        </Typography>
        <Typography variant="body2" sx={{ color: '#666', mb: 3 }}>
          One-time migration. Populates the new <code>parentUserIds</code> array on every athlete
          doc that has a <code>parentUserId</code>. Safe to re-run — already-populated docs are skipped.
        </Typography>

        <Paper elevation={2} sx={{ p: 3, borderRadius: 3, mb: 3 }}>
          <Stack spacing={2}>
            <Typography variant="body2" sx={{ color: '#444' }}>
              <strong>What this does:</strong>
              <ul style={{ marginTop: 4, marginBottom: 0 }}>
                <li>For each athlete with a <code>parentUserId</code> but no <code>parentUserIds</code> array, sets <code>parentUserIds: [parentUserId]</code>.</li>
                <li>For each athlete with both fields but the legacy uid is missing from the array, adds it.</li>
                <li>Athletes with no parent (adult self-records) are skipped.</li>
              </ul>
            </Typography>
            <Button
              variant="contained" startIcon={running ? <CircularProgress size={18} sx={{ color: 'white' }}/> : <PlayArrowIcon />}
              onClick={run} disabled={running}
              sx={{ alignSelf: 'flex-start', background: DARK, fontWeight: 700 }}
            >
              {running ? 'Running…' : 'Run Backfill'}
            </Button>
          </Stack>
        </Paper>

        {error && <Alert severity="error" sx={{ mb: 2 }} onClose={() => setError('')}>{error}</Alert>}

        {result && (
          <Alert severity="success" sx={{ mb: 2 }}>
            <strong>Done.</strong><br />
            Total athletes: {result.total}<br />
            Updated: {result.updated}<br />
            Skipped (already populated): {result.skipped}<br />
            No parent (adult self-records): {result.noParent}
            {Array.isArray(result.errors) && result.errors.length > 0 && (
              <Box sx={{ mt: 1, color: '#C0392B' }}>
                <strong>Batch errors ({result.errors.length}):</strong>
                <ul>{result.errors.map((e, i) => <li key={i}>{e}</li>)}</ul>
              </Box>
            )}
          </Alert>
        )}
      </Box>
    </Layout>
  );
}
